import { useCallback, useState, useMemo } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormik, FormikProps } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { TextInput, AutocompleteInput, CheckboxInput } from '../inputs';
import {
  useDispatch,
  useSelector,
  addResource,
  systemSelectors,
} from '../../../state';
import { useRefresh, useNotify, useGetUsersData } from '../../../hooks';
import { requiresStateCode } from '../../../lib';
import { SelectOption } from '../../../types';
import { InfoMessage } from '../InfoMessage';

interface BatchEditAddressState {
  shipToAddress1: string;
  shipToAddress2: string;
  shipToCity: string;
  shipToStateID: string;
  shipToStateAlternate: string;
  shipToZip: string;
  shipToCountryID: string;
  shipWithFailedAddressValidation: boolean;
}

interface BatchEditAddressFormProps {
  open: boolean;
  handleClose: () => void;
  orderIds?: number[];
  setViewVersion?: (cb: (prev: number) => number) => void;
}

const initialState: BatchEditAddressState = {
  shipToAddress1: '',
  shipToAddress2: '',
  shipToCity: '',
  shipToStateID: '',
  shipToStateAlternate: '',
  shipToZip: '',
  shipToCountryID: '',
  shipWithFailedAddressValidation: false,
};

const schema = object().shape({
  shipToAddress1: string().required('Required'),
  shipToCity: string().required('Required'),
  shipToZip: string().required('Required'),
  shipToCountryID: string().nullable().required('Required'),
  shipToStateID: string().nullable(),
});

export function BatchEditAddressForm({
  open,
  handleClose,
  orderIds,
  setViewVersion,
}: BatchEditAddressFormProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const notify = useNotify();
  const { isAdmin, isCustomer, isCustomerService, isTruckingSalesPerson } =
    useGetUsersData();
  const hasEditPermission =
    isAdmin || isCustomer || isCustomerService || isTruckingSalesPerson;
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [adminToken, setAdminToken] = useState<string | undefined>();
  const [showManagerLogin, setShowManagerLogin] =
    useState<boolean>(!hasEditPermission);
  const states = useSelector(systemSelectors.states);
  const countries = useSelector(systemSelectors.countries);
  const countriesObj = useSelector(systemSelectors.countriesObj);

  const handleSubmit = useCallback(
    async (values: BatchEditAddressState) => {
      setSubmitting(true);
      const { shipToStateAlternate, shipToStateID, ...rest } = values;
      const isStateCodeRequired = requiresStateCode({
        countryId: values.shipToCountryID,
        stateAlternate: values.shipToStateAlternate,
        countriesObj,
      });
      const payload: any = { ...rest, orderIDs: orderIds };
      if (isStateCodeRequired) {
        payload.shipToStateID = shipToStateID;
      } else {
        payload.shipToStateAlternate = shipToStateAlternate;
      }
      const { error } = await dispatch(
        addResource({
          baseUrl: `/batches/edit/order-address`,
          payload,
          message: 'Address updated for all selected orders',
          tokenOverride: adminToken,
        }),
      );

      setSubmitting(false);
      if (!error) {
        refresh();
        setViewVersion?.((prev) => prev + 1);
        handleClose();
      } else {
        notify(
          error.message || error.title || 'Something went wrong',
          'error',
        );
      }
    },
    [adminToken, countriesObj, dispatch, handleClose, notify, orderIds, refresh, setViewVersion],
  );

  const validate = useCallback(
    (values: BatchEditAddressState) => {
      const errors: Partial<Record<keyof BatchEditAddressState, string>> = {};
      const needsStateCode = requiresStateCode({
        countryId: values.shipToCountryID,
        stateAlternate: values.shipToStateAlternate,
        countriesObj,
      });
      if (needsStateCode && !values.shipToStateID) {
        errors.shipToStateID = 'Required';
      }
      return errors;
    },
    [countriesObj],
  );

  const formik: FormikProps<BatchEditAddressState> =
    useFormik<BatchEditAddressState>({
      initialValues: initialState,
      onSubmit: handleSubmit,
      validationSchema: schema,
      validate,
    });

  const onAdminLogin = useCallback((data: string) => {
    setShowManagerLogin(false);
    setAdminToken(data);
  }, []);

  const showAlternateStateInput = useMemo(() => {
    return !requiresStateCode({
      countryId: formik.values.shipToCountryID,
      stateAlternate: formik.values.shipToStateAlternate,
      countriesObj,
    });
  }, [
    countriesObj,
    formik.values.shipToCountryID,
    formik.values.shipToStateAlternate,
  ]);

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Batch update address</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      maxWidth='lg'
      paperProps={{ style: { width: showManagerLogin ? 468 : 720 } }}
      submitting={submitting}
      showManagerLogin={showManagerLogin}
      onAdminLogin={onAdminLogin}
    >
      <div className={classes.layout}>
        {orderIds?.length && (
          <InfoMessage
            message={`The shipping address for all ${orderIds.length} selected orders will be replaced with the address entered below.`}
          />
        )}
        <Grid container spacing={5} rowSpacing={2}>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='shipToAddress1'
              formikProps={formik}
              label='Address line 1'
              className={classes.input}
              autoComplete='new-password'
              required
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='shipToAddress2'
              formikProps={formik}
              label='Address line 2'
              className={classes.input}
              autoComplete='new-password'
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='shipToCity'
              formikProps={formik}
              label='City'
              className={classes.input}
              autoComplete='new-password'
              required
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            {showAlternateStateInput ? (
              <TextInput
                name='shipToStateAlternate'
                formikProps={formik}
                label='Enter state'
                className={classes.input}
                autoComplete='new-password'
                required
              />
            ) : (
              <AutocompleteInput
                formikProps={formik}
                textFieldProps={{
                  name: 'shipToStateID',
                  label: 'State',
                  autoComplete: 'new-password',
                  required: true,
                }}
                autocompleteProps={{
                  options: states as SelectOption<string | number>[],
                }}
              />
            )}
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <TextInput
              name='shipToZip'
              formikProps={formik}
              label='Zip'
              className={classes.input}
              required
            />
          </Grid>
          <Grid
            size={{
              sm: 6,
            }}
          >
            <AutocompleteInput
              formikProps={formik}
              textFieldProps={{
                name: 'shipToCountryID',
                label: 'Country',
                autoComplete: 'new-password',
                required: true,
              }}
              autocompleteProps={{
                options: countries,
              }}
            />
          </Grid>
          <Grid
            size={{
              sm: 12,
            }}
          >
            <CheckboxInput
              formikProps={formik}
              checkboxes={[
                {
                  labelProps: {
                    label:
                      'I approve this invalid address to be saved and used for the order shipment.',
                  },
                  checkboxProps: {
                    name: 'shipWithFailedAddressValidation',
                  },
                },
              ]}
            />
          </Grid>
        </Grid>
      </div>
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
    padding: '24px 16px',
  },
  input: {
    width: '100%',
  },
}));
